import { HttpEvent, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { PartnerService } from '../services/partner.service';
import { catchError, from, Observable, switchMap, throwError } from 'rxjs';

const EXCLUDE_PATHS = [
  '/partners/requests',
  '/partners/activation/otp',
  '/partners/activate',
  '/partners/login'
];

const TIMEOUT_EXCLUDE_PATHS = [
  '/partners/logout'
];

const UNSAFE_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

function isPublicApi(url: string): boolean {
  return EXCLUDE_PATHS.some((path) => url.includes(path));
}

function shouldTriggerTimeout(url: string): boolean {
  if (isPublicApi(url)) {
    return false;
  }
  return !TIMEOUT_EXCLUDE_PATHS.some((path) => url.includes(path));
}

export const partnerInterceptor: HttpInterceptorFn = (req, next) => {
  const partnerService = inject(PartnerService);

  // Always send credentials so the browser stores the session + CSRF cookies set
  // on login and sends them back on subsequent calls (the app and API are
  // cross-origin). Auth now rides the httpOnly session cookie — no Bearer header.
  const baseReq = req.clone({ withCredentials: true });

  if (isPublicApi(req.url)) {
    return next(baseReq);
  }

  const needsCsrf = UNSAFE_METHODS.includes(req.method);

  const send = (): Observable<HttpEvent<unknown>> => {
    let outgoing = baseReq;
    const csrf = partnerService.csrfToken();
    if (needsCsrf && csrf) {
      outgoing = outgoing.clone({
        setHeaders: {
          'x-csrf-token': csrf
        }
      });
    }

    return next(outgoing).pipe(
      catchError((err) => {
        if (shouldTriggerTimeout(req.url) && (err?.status === 401 || err?.status === 403)) {
          partnerService.handleSessionTimeout();
        }
        return throwError(() => err);
      })
    );
  };

  // Block a mutating request until the CSRF token is in memory (e.g. right after
  // a page reload, when only the httpOnly cookie survives).
  if (needsCsrf && !partnerService.csrfToken()) {
    return from(partnerService.ensureCsrfReady()).pipe(switchMap(() => send()));
  }

  return send();
};
